import { Button } from "@/components/ui/button";
import { api, type ContainerRow, type Permissions } from "@/lib/api";
import { containerPermissions } from "@/lib/boo-permissions";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useConnectionOutcomes } from "./use-connection-outcomes";
import { CodeHostsCard } from "./CodeHostsCard";
import { GoogleChatConnection } from "./GoogleChatConnection";
import { GoogleWorkspaceConnection } from "./GoogleWorkspaceConnection";
import { SharedChatAppConnection } from "./SharedChatAppConnection";
import { SlackConnection } from "./SlackConnection";
import { ConnectionHeader, StatusLine } from "./ConnectionParts";
import { findConnection, useConnectionStatuses, type ConnectionDef } from "./connections-lib";
import { Microsoft365Settings } from "./Microsoft365Settings";

// …/containers/$containerId/connections/$connection: one connection's page, like
// Devin's Settings → Connections → GitHub. The header names it and says where
// it stands; the body is that connection's own setup.
export function ConnectionPage() {
  const params = useParams({ strict: false }) as {
    orgId?: string;
    containerId?: string;
    connection?: string;
  };
  const { data: listResponse } = useQuery({ queryKey: ["containers"], queryFn: api.containers.list });
  const container = listResponse?.data.find((c) => c.id === params.containerId);
  const permissions = containerPermissions(container, listResponse?._permissions);
  const def = findConnection(params.connection);
  useConnectionOutcomes(params.containerId ?? "");

  if (!container || !params.orgId) return null;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto">
      <div className="flex w-full max-w-3xl flex-col gap-6 p-6">
        <div>
          <Button asChild size="sm" variant="ghost" className="-ml-2 gap-1.5 text-muted-foreground">
            <Link to="/orgs/$orgId/containers/$containerId" params={{ orgId: params.orgId, containerId: container.id }}>
              <ArrowLeft className="size-4" />
              Connections
            </Link>
          </Button>
        </div>
        {def ? (
          <ConnectionBody def={def} container={container} permissions={permissions} />
        ) : (
          <div className="rounded-lg border p-6 text-center text-sm text-muted-foreground">
            There's no connection here.
          </div>
        )}
      </div>
    </div>
  );
}

function ConnectionBody({
  def,
  container,
  permissions,
}: {
  def: ConnectionDef;
  container: ContainerRow;
  permissions: Permissions;
}) {
  const statuses = useConnectionStatuses(container);
  const status = statuses[def.id];
  if (!status) {
    return (
      <>
        <ConnectionHeader def={def} />
        <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
          {def.name} isn&apos;t available on this Boo.
        </div>
      </>
    );
  }

  // Each page draws its own header: its status and actions (Disconnect,
  // Release) live there.
  switch (def.id) {
    case "microsoft-365":
      return <Microsoft365Settings entry={def} container={container} />;
    case "slack":
      return <SlackConnection def={def} container={container} permissions={permissions} />;
    case "microsoft-teams":
      return <SharedChatAppConnection def={def} container={container} platform="teams" />;
    case "discord":
      return <SharedChatAppConnection def={def} container={container} platform="discord" />;
    case "google-chat":
      return <GoogleChatConnection def={def} container={container} permissions={permissions} />;
    case "google-workspace":
      return <GoogleWorkspaceConnection def={def} container={container} permissions={permissions} />;
    case "github":
    case "gitlab":
      return (
        <>
          <ConnectionHeader def={def} status={<StatusLine status={status} className="h-5 text-sm" />} />
          <p className="max-w-2xl text-sm text-muted-foreground">{def.description}</p>
          <CodeHostsCard container={container} canEdit={permissions?.canEdit === true} host={def.id} />
        </>
      );
  }
}
